// modules/freshcards/src/LayoutAdapter.ts
//
// 2026-09-03 (Phase 13) — Layout Adapter.
//
// Converts an external app's layout (CSS grid, Material layout,
// a JSON description, a flexbox config) into a FreshCards Layout
// object. The result is fed to LayoutAPI.saveLayout and applied
// via ThemeEngine.applyLayout.
//
// The adapter is PURE: it accepts a layout descriptor and returns
// a Layout. No DOM access. The caller (host app) is responsible
// for reading the source format and feeding it in.

import type { Layout } from '../embedding/LayoutAPI';
import { DEFAULT_LAYOUT } from '../embedding/LayoutAPI';

// ---------------------------------------------------------------------------
// CSS Grid adapter
// ---------------------------------------------------------------------------

export interface GridDescriptor {
  /** Total columns. */
  columns: number;
  /** Column width in px or 'auto'. */
  columnWidth?: number | 'auto';
  /** Gutter in px. */
  gap?: number;
  /** Max container width in px. */
  maxWidth?: number;
  /** Per-section columns. */
  sections?: Array<{
    name: string;
    columns: number;
    blocks?: Array<{
      kind: 'heading' | 'paragraph' | 'reference' | 'semantic' | 'template' | 'database' | 'custom';
      columns?: number;
    }>;
  }>;
}

export function gridToLayout(grid: GridDescriptor): Layout {
  const density = (grid.gap ?? 12) <= 6 ? 'compact' : (grid.gap ?? 12) >= 18 ? 'spacious' : 'comfortable';
  const spacing = (grid.gap ?? 8) <= 4 ? 'tight' : (grid.gap ?? 8) >= 14 ? 'relaxed' : 'standard';
  let pageWidth: Layout['pageWidth'] = 'standard';
  if (grid.maxWidth) {
    if (grid.maxWidth <= 640) pageWidth = 'narrow';
    else if (grid.maxWidth <= 900) pageWidth = 'standard';
    else if (grid.maxWidth <= 1100) pageWidth = 'wide';
    else pageWidth = 'full';
  }
  return {
    ...DEFAULT_LAYOUT,
    pageWidth,
    spacing,
    density,
    geometry: {
      ...DEFAULT_LAYOUT.geometry,
      radius: (grid.gap ?? 8) >= 12 ? 12 : 6,
    },
  };
}

// ---------------------------------------------------------------------------
// Flexbox descriptor
// ---------------------------------------------------------------------------

export interface FlexDescriptor {
  direction: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  gap?: number;
  padding?: number;
  wrap?: 'nowrap' | 'wrap';
  align?: 'start' | 'center' | 'end' | 'stretch';
  justify?: 'start' | 'center' | 'end' | 'space-between' | 'space-around';
  width?: number | string;
}

export function flexToLayout(flex: FlexDescriptor): Layout {
  const gap = flex.gap ?? 8;
  const density = gap <= 6 ? 'compact' : gap >= 18 ? 'spacious' : 'comfortable';
  const spacing = gap <= 4 ? 'tight' : gap >= 14 ? 'relaxed' : 'standard';
  const pageWidth: Layout['pageWidth'] =
    flex.direction === 'row' ? 'wide'
      : flex.direction === 'column' ? 'standard'
      : 'full';
  return {
    ...DEFAULT_LAYOUT,
    pageWidth,
    spacing,
    density,
    geometry: {
      ...DEFAULT_LAYOUT.geometry,
      radius: 8,
      padding: flex.padding ?? DEFAULT_LAYOUT.geometry.padding,
    },
  };
}

// ---------------------------------------------------------------------------
// Material-like layout
// ---------------------------------------------------------------------------

export interface MaterialLayoutDescriptor {
  /** Topbar present? */
  hasTopBar?: boolean;
  /** Navigation rail position. */
  navRail?: 'left' | 'right' | 'none';
  /** FAB present? */
  hasFab?: boolean;
  /** Body padding. */
  bodyPadding?: number;
  /** Column count. */
  columns?: number;
}

export function materialToLayout(desc: MaterialLayoutDescriptor): Layout {
  const sidebar: Layout['sidebar'] = desc.navRail === 'right' ? 'right' : desc.navRail === 'none' ? 'hidden' : 'left';
  const pageHeader: Layout['pageHeader'] = desc.hasTopBar ? 'hero' : 'standard';
  return {
    ...DEFAULT_LAYOUT,
    sidebar,
    pageHeader,
    spacing: 'standard',
    density: 'comfortable',
    geometry: {
      ...DEFAULT_LAYOUT.geometry,
      padding: desc.bodyPadding ?? DEFAULT_LAYOUT.geometry.padding,
      radius: 12,
    },
  };
}

// ---------------------------------------------------------------------------
// Section → block group mapping
//
// External apps may declare "sections" that FreshCards renders as
// semantic groups inside a page. The adapter converts section
// descriptors to PageBlock[].
// ---------------------------------------------------------------------------

export interface SectionDescriptor {
  name: string;
  title: string;
  blocks: Array<{
    type: 'heading' | 'paragraph' | 'reference' | 'semantic' | 'template' | 'database' | 'custom';
    data: Record<string, any>;
  }>;
}

export function sectionsToBlockGroups(sections: SectionDescriptor[]): Array<{ sectionId: string; title: string; blocks: any[] }> {
  return sections.map(s => ({
    sectionId: `sec-${s.name.toLowerCase().replace(/\s+/g, '-')}`,
    title: s.title,
    blocks: s.blocks.map(b => ({
      ...b,
      id: `${b.type}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
    })),
  }));
}
